import React, { Fragment, useCallback, useEffect, useState } from 'react'
import ApiService from '../../../../services/ApiService'
import BuyOrderSection, {
  BuyOrderBadge,
} from '../../../../components/ProductionFlow/ViewOPServiceModal/BuyOrderSection'
import {
  SimpleTable,
  SimpleTableRow,
  TableHeader,
  TableCell,
  Section,
  SectionDivider,
  SectionSubtitle,
} from '../../../../components/ProductionFlow/ViewOPServiceModal/style'

interface BuyOrderData {
  id: number
  stockInProductionId: number
  quantityRequired: number | null
  quantityToBuy: number | null
  unitPrice: number | null
  totalPrice: number | null
  unitOfMeasurement: string | null
  status: string
  observations: string | null
  createdAt: string
  CreatedBy?: { id: number; name: string }
  StockInProduction?: {
    id: number
    stockId: number | null
    resourceId: number | null
    usedQuantity: number
    usedUnitOfMeasurement: string
    Stock?: { id: number; Code?: { code?: string } | null } | null
    Resource?: { id: number; name?: string } | null
  }
}

interface FashionBuyOrdersSectionProps {
  servicesId: number
}

const getMaterialName = (stockItem: any): string => {
  const stock = stockItem?.Stock
  return (
    stock?.Code?.code ||
    (stockItem?.stockId ? `Material ID: ${stockItem.stockId}` : 'Material não identificado')
  )
}

const getResourceName = (stockItem: any): string => {
  const resource = stockItem?.Resource
  return (
    resource?.name ||
    (stockItem?.resourceId ? `Insumo ID: ${stockItem.resourceId}` : 'Insumo não identificado')
  )
}

const FashionBuyOrdersSection: React.FC<FashionBuyOrdersSectionProps> = ({
  servicesId,
}) => {
  const [buyOrders, setBuyOrders] = useState<BuyOrderData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({})

  const fetchBuyOrders = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.buyOrder.getByProduction(servicesId)
      setBuyOrders(response.data || [])
    } catch (e) {
      console.error('Erro ao carregar ordens de compra:', e)
      setError('Falha ao carregar ordens de compra.')
    } finally {
      setIsLoading(false)
    }
  }, [servicesId])

  useEffect(() => {
    fetchBuyOrders()
  }, [fetchBuyOrders])

  const toggleRow = (stockInProductionId: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [stockInProductionId]: !prev[stockInProductionId],
    }))
  }

  const getBuyOrderFor = (stockInProductionId: number) =>
    buyOrders.find((bo) => bo.stockInProductionId === stockInProductionId) || null

  const stockInProductions = buyOrders
    .map((bo) => bo.StockInProduction)
    .filter((s): s is NonNullable<typeof s> => Boolean(s))

  const seen = new Set<number>()
  const dedupStockInProductions = stockInProductions.filter((s) => {
    if (seen.has(s.id)) return false
    seen.add(s.id)
    return true
  })

  const materials = dedupStockInProductions.filter((s) => s.stockId !== null)
  const resources = dedupStockInProductions.filter((s) => s.resourceId !== null)

  if (isLoading) {
    return (
      <Section>
        <SectionSubtitle>Ordens de Compra</SectionSubtitle>
        <p style={{ color: '#666', padding: '12px 0' }}>Carregando OCs…</p>
      </Section>
    )
  }

  if (error) {
    return (
      <Section>
        <SectionSubtitle>Ordens de Compra</SectionSubtitle>
        <p style={{ color: '#c53030', padding: '12px 0' }}>{error}</p>
      </Section>
    )
  }

  if (dedupStockInProductions.length === 0) {
    return (
      <Section>
        <SectionSubtitle>Ordens de Compra</SectionSubtitle>
        <p style={{ color: '#666', padding: '12px 0' }}>
          Nenhuma Ordem de Compra associada a esta OP.
        </p>
      </Section>
    )
  }

  const renderTable = (
    title: string,
    rows: typeof dedupStockInProductions,
    getName: (s: any) => string,
  ) => {
    const tableColSpan = 4
    return (
      <Section>
        <SectionSubtitle>{title}</SectionSubtitle>
        {rows.length > 0 ? (
          <SimpleTable className="compact-table">
            <thead>
              <tr>
                <TableHeader>{title === 'Materiais' ? 'Material' : 'Insumo'}</TableHeader>
                <TableHeader style={{ textAlign: 'center', width: '120px' }}>
                  Un. medida
                </TableHeader>
                <TableHeader style={{ textAlign: 'right', width: '100px' }}>Qtd.</TableHeader>
                <TableHeader style={{ textAlign: 'center', width: '120px' }}>OC</TableHeader>
              </tr>
            </thead>
            <tbody>
              {rows.map((stockItem) => (
                <Fragment key={stockItem.id}>
                  <SimpleTableRow
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleRow(stockItem.id)}
                  >
                    <TableCell>{getName(stockItem)}</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>
                      {stockItem.usedUnitOfMeasurement || '-'}
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      {stockItem.usedQuantity ?? '-'}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }}>
                      <BuyOrderBadge
                        buyOrder={getBuyOrderFor(stockItem.id) as any}
                        onClick={() => toggleRow(stockItem.id)}
                      />
                    </TableCell>
                  </SimpleTableRow>
                  {expandedRows[stockItem.id] && (
                    <BuyOrderSection
                      stockInProductionId={stockItem.id}
                      productionId={servicesId}
                      existingBuyOrder={getBuyOrderFor(stockItem.id) as any}
                      onBuyOrderChange={fetchBuyOrders}
                      colSpan={tableColSpan}
                    />
                  )}
                </Fragment>
              ))}
            </tbody>
          </SimpleTable>
        ) : (
          <p style={{ textAlign: 'center', color: '#666', padding: '24px 0' }}>
            Nenhum item cadastrado.
          </p>
        )}
      </Section>
    )
  }

  return (
    <>
      <SectionDivider />
      {renderTable('Materiais', materials, getMaterialName)}
      <SectionDivider />
      {renderTable('Insumos', resources, getResourceName)}
    </>
  )
}

export default FashionBuyOrdersSection
